import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { STRIPES, dayInitial, habitLabel, lastMonth, nextStatus, stripDays, streak, ymd } from "@/lib/habits";

const cellClass = (status, isToday, future) => {
  if (future) return "border border-linea text-meta opacity-50 cursor-not-allowed";
  const border = isToday ? "border-[1.5px] border-dashed border-acento" : status === "si" ? "border border-tinta" : "border border-borde";
  return `${border} ${status === "si" ? "bg-tinta text-sobre-tinta" : "text-tinta"}`;
};

const Swatch = ({ status }) => (
  <span className={`inline-block h-3 w-3 rounded-[3px] align-[-1px] ${status === "si" ? "bg-tinta" : "border border-borde"}`} style={status === "no" ? STRIPES : undefined} aria-hidden="true" />
);

const StripLegend = ({ id, locked }) => (
  <p className="text-[12px] text-meta mt-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`habit-legend-${id}`}>
    <span className="inline-flex items-center gap-1.5"><Swatch status="si" /> Lo hice</span>
    <span className="inline-flex items-center gap-1.5"><Swatch status="no" /> Volví al anterior / Hoy no</span>
    <span className="inline-flex items-center gap-1.5"><Swatch /> Sin marcar</span>
    {!locked && <span>· Toca un día para corregirlo</span>}
  </p>
);

const DayStrip = ({ habit, byDate, onSet, locked }) => {
  const today = ymd(new Date());
  const { early, days } = stripDays(habit.created_at);
  return (
    <div className="mt-5">
      <p className="text-[13px] text-sec mb-2" data-testid={`habit-strip-title-${habit.id}`}>{early ? "Tus primeros 14 días" : "Tus últimos 14 días"}</p>
      <div className="flex gap-0.5 sm:gap-1 pb-1" data-testid={`habit-strip-${habit.id}`}>
        {days.map((d) => {
          const k = ymd(d);
          const s = byDate[k];
          const future = k > today;
          return (
            <div key={k} className="flex-1 min-w-0 sm:min-w-[22px] flex flex-col items-center gap-1">
              <button type="button" disabled={future || locked} onClick={() => onSet(k, nextStatus(s))}
                aria-label={`${k}: ${future ? "todavía no llega" : s === "si" ? "lo hice" : s === "no" ? "no" : "sin marcar"}`}
                data-testid={`habit-day-${habit.id}-${k}`} style={s === "no" && !future ? STRIPES : undefined}
                className={`h-9 w-full rounded-[6px] text-[11px] disabled:cursor-not-allowed ${cellClass(s, k === today, future)} ${locked && !future ? "opacity-50" : ""}`}>
                <span className={`inline-flex h-4 min-w-4 items-center justify-center rounded-[4px] px-0.5 ${s === "no" && !future ? "bg-superficie" : ""}`}>{dayInitial(d)}</span>
              </button>
              <span className={`text-[11px] leading-none ${k === today ? "text-tinta font-medium" : future ? "text-meta opacity-50" : "text-meta"}`}>
                {k === today ? "hoy" : d.getDate()}
              </span>
            </div>
          );
        })}
      </div>
      <StripLegend id={habit.id} locked={locked} />
    </div>
  );
};

const DeleteHabit = ({ onConfirm, id }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button type="button" className="btn-text" onClick={() => (armed ? onConfirm() : setArmed(true))} data-testid={`habit-delete-${id}`}>
      {armed ? "Toca otra vez para borrarlo para siempre" : "Borrar"}
    </button>
  );
};

export const HabitCard = ({ habit, byDate, onSet, onEdit, onDelete, showCounts = true }) => {
  const navigate = useNavigate();
  const today = ymd(new Date());
  const s = byDate[today];
  const days = streak(byDate);
  const month = lastMonth(byDate, habit.created_at);
  const writeAbout = () => {
    const id = crypto.randomUUID();
    navigate(`/escribir/${id}`, { state: { nueva: id, title: `Mi hábito: ${habit.habit}` } });
  };
  return (
    <article className="min-w-0 rounded-[24px] bg-superficie p-5 sm:p-6" data-testid={`habit-card-${habit.id}`}>
      <p className="eyebrow text-xs">{habitLabel(habit)}</p>
      <p className="font-serif text-[22px] leading-snug text-tinta mt-2" data-testid={`habit-text-${habit.id}`}>{habit.habit}</p>
      <div className="flex flex-wrap gap-2 mt-5">
        <button type="button" disabled={!showCounts} aria-pressed={s === "si"} onClick={() => onSet(today, s === "si" ? null : "si")} data-testid={`habit-done-${habit.id}`}
          className={`disabled:opacity-50 rounded-full border-[1.5px] px-5 py-2.5 text-[15px] font-medium ${s === "si" ? "bg-tinta text-sobre-tinta border-tinta" : "border-borde text-tinta hover:bg-resaltado"}`}>
          Lo hice
        </button>
        <button type="button" disabled={!showCounts} aria-pressed={s === "no"} onClick={() => onSet(today, s === "no" ? null : "no")} data-testid={`habit-not-${habit.id}`}
          style={s === "no" ? STRIPES : undefined}
          className={`disabled:opacity-50 rounded-full border-[1.5px] px-1.5 py-1.5 text-[15px] font-medium text-tinta ${s === "no" ? "border-tinta" : "border-borde hover:bg-resaltado"}`}>
          <span className={`block rounded-full px-3.5 py-1 ${s === "no" ? "bg-superficie" : ""}`}>{habit.type === "cambiar" ? "Volví al anterior" : "Hoy no"}</span>
        </button>
      </div>
      <DayStrip habit={habit} byDate={byDate} onSet={onSet} locked={!showCounts} />
      {showCounts && (
        <p className="text-[14px] text-sec mt-4" data-testid={`habit-stats-${habit.id}`}>
          {days} {days === 1 ? "día seguido" : "días seguidos"} · {month.done} de {month.total} días marcados en el último mes
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1 mt-4 -ml-2">
        <button type="button" className="btn-text" onClick={writeAbout} data-testid={`habit-write-${habit.id}`}>Escribir sobre esto</button>
        <button type="button" className="btn-text" onClick={onEdit} data-testid={`habit-edit-${habit.id}`}>Editar</button>
        <DeleteHabit onConfirm={onDelete} id={habit.id} />
      </div>
    </article>
  );
};
